import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Save,
  Eye,
  Smartphone,
  Monitor,
  Palette,
  Image,
  Link,
  Plus,
  Trash2,
  Edit3,
  Upload,
  Globe,
  Mail,
  Share2,
  Video,
  MessageCircle
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const LinkInBioBuilder = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('mobile'); // mobile or desktop
  const [activeTemplate, setActiveTemplate] = useState('minimal');
  
  // Bio page configuration
  const [bioConfig, setBioConfig] = useState({
    title: user?.full_name || 'My Links',
    description: 'Check out my links below!',
    profile_image: '',
    background_color: '#ffffff',
    text_color: '#333333',
    accent_color: '#f59e0b',
    template: 'minimal',
    links: [
      {
        id: '1',
        title: 'My Website',
        url: 'https://example.com',
        icon: 'globe',
        is_active: true
      }
    ],
    social_links: {
      instagram: '',
      twitter: '',
      facebook: '',
      youtube: '',
      tiktok: '',
      linkedin: ''
    }
  });

  const templates = [
    {
      id: 'minimal',
      name: 'Minimal',
      description: 'Clean and simple design',
      preview: '/api/templates/minimal-preview.jpg'
    },
    {
      id: 'gradient',
      name: 'Gradient',
      description: 'Modern gradient background',
      preview: '/api/templates/gradient-preview.jpg'
    },
    {
      id: 'business',
      name: 'Business',
      description: 'Professional business look',
      preview: '/api/templates/business-preview.jpg'
    },
    {
      id: 'creative',
      name: 'Creative',
      description: 'Colorful and artistic',
      preview: '/api/templates/creative-preview.jpg'
    }
  ];

  const iconOptions = [
    { value: 'globe', label: 'Website', icon: Globe },
    { value: 'instagram', label: 'Instagram', icon: Share2 },
    { value: 'twitter', label: 'Twitter', icon: MessageCircle },
    { value: 'facebook', label: 'Facebook', icon: Share2 },
    { value: 'youtube', label: 'YouTube', icon: Video },
    { value: 'tiktok', label: 'TikTok', icon: Video },
    { value: 'linkedin', label: 'LinkedIn', icon: Share2 }
  ];

  useEffect(() => {
    loadExistingBio();
  }, []);

  const loadExistingBio = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/link-in-bio`);
      if (response.ok) {
        const data = await response.json();
        if (data.bio) {
          setBioConfig(data.bio);
          setActiveTemplate(data.bio.template);
        }
      }
    } catch (error) {
      console.error('Error loading bio:', error);
    }
  };

  const handleAddLink = () => {
    const newLink = {
      id: Date.now().toString(),
      title: 'New Link',
      url: 'https://',
      icon: 'globe',
      is_active: true
    };
    
    setBioConfig(prev => ({
      ...prev,
      links: [...prev.links, newLink]
    }));
  };

  const handleUpdateLink = (linkId, field, value) => {
    setBioConfig(prev => ({
      ...prev,
      links: prev.links.map(link => 
        link.id === linkId ? { ...link, [field]: value } : link
      )
    }));
  };

  const handleDeleteLink = (linkId) => {
    setBioConfig(prev => ({
      ...prev,
      links: prev.links.filter(link => link.id !== linkId)
    }));
  };

  const handleSaveBio = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/link-in-bio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bioConfig)
      });

      if (response.ok) {
        toast.success('Link-in-Bio page saved successfully!');
      } else {
        toast.error('Failed to save bio page');
      }
    } catch (error) {
      console.error('Error saving bio:', error);
      toast.error('Failed to save bio page');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setActiveTemplate(templateId);
    setBioConfig(prev => ({
      ...prev,
      template: templateId
    }));
  };

  const getIconComponent = (iconName) => {
    const IconComponent = iconOptions.find(opt => opt.value === iconName)?.icon || Globe;
    return <IconComponent className="h-4 w-4" />;
  };

  // Bio Preview Component
  const BioPreview = () => (
    <div className={`${previewMode === 'mobile' ? 'max-w-sm' : 'max-w-md'} mx-auto border rounded-lg overflow-hidden shadow-lg`}>
      <div 
        className="p-6 text-center"
        style={{ 
          backgroundColor: bioConfig.background_color,
          color: bioConfig.text_color 
        }}
      >
        {/* Profile Image */}
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
          {bioConfig.profile_image ? (
            <img 
              src={bioConfig.profile_image} 
              alt="Profile" 
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <div className="text-2xl font-bold text-gray-500">
              {bioConfig.title.charAt(0)}
            </div>
          )}
        </div>

        {/* Title and Description */}
        <h2 className="text-xl font-bold mb-2">{bioConfig.title}</h2>
        <p className="text-sm opacity-80 mb-6">{bioConfig.description}</p>

        {/* Links */}
        <div className="space-y-3">
          {bioConfig.links.filter(link => link.is_active).map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full p-3 rounded-lg border transition-colors hover:opacity-80"
              style={{ 
                borderColor: bioConfig.accent_color,
                backgroundColor: `${bioConfig.accent_color}20`
              }}
            >
              <div className="flex items-center justify-center space-x-2">
                {getIconComponent(link.icon)}
                <span className="font-medium">{link.title}</span>
              </div>
            </a>
          ))}
        </div>

        {/* Social Links */}
        <div className="flex justify-center space-x-4 mt-6">
          {Object.entries(bioConfig.social_links)
            .filter(([key, value]) => value)
            .map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full"
                style={{ backgroundColor: `${bioConfig.accent_color}30` }}
              >
                {getIconComponent(platform)}
              </a>
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Link-in-Bio Builder</h1>
            <p className="text-gray-600 mt-1">
              Create your personalized bio page for social media
            </p>
          </div>
          
          <div className="flex space-x-3">
            <Button variant="outline" className="flex items-center">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button 
              onClick={handleSaveBio}
              disabled={loading}
              className="btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Bio'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor Panel */}
          <div className="space-y-6">
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="design">Design</TabsTrigger>
                <TabsTrigger value="links">Links</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Basic Information</CardTitle>
                    <CardDescription>
                      Set your profile title and description
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Profile Title</Label>
                      <Input
                        id="title"
                        value={bioConfig.title}
                        onChange={(e) => setBioConfig(prev => ({...prev, title: e.target.value}))}
                        placeholder="Your name or brand"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={bioConfig.description}
                        onChange={(e) => setBioConfig(prev => ({...prev, description: e.target.value}))}
                        placeholder="Brief description about yourself"
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="profile_image">Profile Image URL</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="profile_image"
                          value={bioConfig.profile_image}
                          onChange={(e) => setBioConfig(prev => ({...prev, profile_image: e.target.value}))}
                          placeholder="https://example.com/image.jpg"
                        />
                        <Button variant="outline" size="sm">
                          <Upload className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Design Tab */}
              <TabsContent value="design" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Templates</CardTitle>
                    <CardDescription>
                      Choose a template and customize colors
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {templates.map((template) => (
                        <div
                          key={template.id}
                          onClick={() => handleTemplateChange(template.id)}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            activeTemplate === template.id 
                              ? 'border-yellow-500 bg-yellow-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="w-full h-20 bg-gray-100 rounded mb-2"></div>
                          <h4 className="font-medium text-sm">{template.name}</h4>
                          <p className="text-xs text-gray-600">{template.description}</p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bg_color">Background</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="bg_color"
                            type="color"
                            value={bioConfig.background_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, background_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={bioConfig.background_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, background_color: e.target.value}))}
                            placeholder="#ffffff"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="text_color">Text Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="text_color"
                            type="color"
                            value={bioConfig.text_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, text_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={bioConfig.text_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, text_color: e.target.value}))}
                            placeholder="#333333"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accent_color">Accent Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="accent_color"
                            type="color"
                            value={bioConfig.accent_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, accent_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={bioConfig.accent_color}
                            onChange={(e) => setBioConfig(prev => ({...prev, accent_color: e.target.value}))}
                            placeholder="#f59e0b"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Links Tab */}
              <TabsContent value="links" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Links</CardTitle>
                      <CardDescription>
                        Add and manage your links
                      </CardDescription>
                    </div>
                    <Button onClick={handleAddLink} size="sm" className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Link
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {bioConfig.links.map((link, index) => (
                      <div key={link.id} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Link {index + 1}</span>
                          <div className="flex space-x-2">
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="checkbox"
                                checked={link.is_active}
                                onChange={(e) => handleUpdateLink(link.id, 'is_active', e.target.checked)}
                                className="rounded"
                              />
                              <span>Active</span>
                            </label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLink(link.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label>Title</Label>
                            <Input
                              value={link.title}
                              onChange={(e) => handleUpdateLink(link.id, 'title', e.target.value)}
                              placeholder="Link title"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>URL</Label>
                            <Input
                              value={link.url}
                              onChange={(e) => handleUpdateLink(link.id, 'url', e.target.value)}
                              placeholder="https://example.com"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Icon</Label>
                            <select
                              value={link.icon}
                              onChange={(e) => handleUpdateLink(link.id, 'icon', e.target.value)}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              {iconOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Social Tab */}
              <TabsContent value="social" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Social Media Links</CardTitle>
                    <CardDescription>
                      Add your social media profiles
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.keys(bioConfig.social_links).map((platform) => (
                      <div key={platform} className="space-y-2">
                        <Label htmlFor={platform} className="capitalize flex items-center">
                          {getIconComponent(platform)}
                          <span className="ml-2">{platform}</span>
                        </Label>
                        <Input
                          id={platform}
                          value={bioConfig.social_links[platform]}
                          onChange={(e) => setBioConfig(prev => ({
                            ...prev,
                            social_links: {
                              ...prev.social_links,
                              [platform]: e.target.value
                            }
                          }))}
                          placeholder={`Your ${platform} URL`}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Preview</CardTitle>
                  <div className="flex space-x-2">
                    <Button
                      variant={previewMode === 'mobile' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewMode('mobile')}
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={previewMode === 'desktop' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewMode('desktop')}
                    >
                      <Monitor className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <BioPreview />
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Your Link:</strong> bbcquicklist.com/bio/{user?.id}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2 w-full"
                    onClick={() => {
                      navigator.clipboard.writeText(`bbcquicklist.com/bio/${user?.id}`);
                      toast.success('Link copied to clipboard!');
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkInBioBuilder;